import React, { useEffect, useState } from 'react'
import urls from 'src/dataLayer/localData/urls'
import { trackAuthenticationResetPassword } from 'src/dataLayer/remoteData/babbelEventStream'
import {
  ForgotPasswordPageCard,
  ForgotPasswordPageContainer,
  ForgotPasswordPageDescriptionContainer,
  ForgotPasswordPageFormContainer,
  ForgotPasswordPageTermsContainer,
} from 'src/viewLayer/components/App/index.styled'
import { CaptchaPasswordReset } from 'src/viewLayer/components/CaptchaPasswordReset/CaptchaPasswordReset'
import { useAppConfigContext } from 'src/viewLayer/components/contexts/AppConfigContext'
import { Text, Title } from 'src/viewLayer/components/DesignSystem'
import isEmailValid from 'src/viewLayer/components/flows/EmailLoginFlow/isEmailValid'
import useResetPasswordRequest from 'src/viewLayer/components/hooks/useResetPasswordRequest'
import BackButton from 'src/viewLayer/components/pages/BackButton'
import Footer from 'src/viewLayer/components/pages/CardLayout/Footer'
import EmailInput from 'src/viewLayer/components/shared/Inputs/EmailInput'
import { InterpolatedLink } from 'src/viewLayer/components/shared/InterpolateLink/index.styled'
import { MagicLink } from 'src/viewLayer/components/shared/MagicLink/MagicLink'
import TrackableButton from 'src/viewLayer/components/shared/TrackableButton'
import { t } from 'src/viewLayer/helpers/translations'
import { showMagicLink } from 'src/viewLayer/navigation'

import NavBar from './NavBar'

const STATUS_IDLE = 'idle'
const STATUS_LOADING = 'loading'
const STATUS_SUCCESS = 'success'

export default function ForgotPasswordPage() {
  const { devicePath } = useAppConfigContext()
  const isNativeMobile = devicePath === 'login-mobile'
  const [resetPasswordRequestStatus, setResetPasswordRequestStatus] = useState(STATUS_IDLE)
  const [captchaToken, setCaptchaToken] = useState()
  const [isButtonClicked, setIsButtonClicked] = useState(false)
  const [email, setEmail] = useState('')
  const [errorKey, setErrorKey] = useState('')
  const { isResetPasswordRequestInProgress, makeResetPasswordRequest } = useResetPasswordRequest({
    captchaResponseToken: captchaToken,
    email,
  })
  const isActionInProgress = isResetPasswordRequestInProgress || isButtonClicked
  const params = new URLSearchParams(globalThis.location.search)

  useEffect(() => {
    const emailParameter = params.get('email')
    if (emailParameter && isEmailValid(emailParameter)) {
      setEmail(emailParameter)
    }
    trackAuthenticationResetPassword({ stage: 'token-request-page-shown' })
  }, [])

  useEffect(() => {
    if (resetPasswordRequestStatus === 'success') {
      trackAuthenticationResetPassword({ stage: 'token-request-success-page-shown' })
    }
  }, [resetPasswordRequestStatus])

  useEffect(() => {
    if (!captchaToken || !isButtonClicked) {
      return
    }
    setIsButtonClicked(false)
    ;(async () => {
      setResetPasswordRequestStatus(STATUS_LOADING)
      const result = await makeResetPasswordRequest()
      if (result?.error) {
        setErrorKey(t(result.error))
        setResetPasswordRequestStatus(STATUS_IDLE)
        return
      }
      setResetPasswordRequestStatus(STATUS_SUCCESS)
    })()
  }, [captchaToken, isButtonClicked, makeResetPasswordRequest])

  const PrivacyPolicyLink = (
    <InterpolatedLink href={urls.privacyPolicyURL} size="xsmall" target="_blank" weight="strong">
      {t('forgot_password_page.terms_and_conditions.privacy_policy_link')}
    </InterpolatedLink>
  )
  const termsOfServiceLink = (
    <InterpolatedLink href={urls.termsOfServiceURL} size="xsmall" target="_blank" weight="strong">
      {t('forgot_password_page.terms_and_conditions.terms_service_link')}
    </InterpolatedLink>
  )

  const handleResetPassword = () => {
    setIsButtonClicked(true)
  }

  return (
    <>
      {!isNativeMobile && <NavBar />}
      <ForgotPasswordPageContainer>
        <BackButton
          path="forgot-password"
          trackingId="backbutton-forgot-password-page-to-password-page"
        />
        <ForgotPasswordPageCard gap={24} padding={{ small: [60, 8, 24], xxsmall: [36, 8, 10] }}>
          <Title size="small" textAlign="center">
            {t('forgot_password_page.title')}
          </Title>
          {resetPasswordRequestStatus === 'success' ? (
            <ForgotPasswordPageDescriptionContainer>
              <Text textAlign="center">
                {t('forgot_password_page.success_confirmation', { email })}
              </Text>
            </ForgotPasswordPageDescriptionContainer>
          ) : (
            <>
              <ForgotPasswordPageDescriptionContainer>
                <Text textAlign="center">{t('forgot_password_page.description')}</Text>
              </ForgotPasswordPageDescriptionContainer>
              <ForgotPasswordPageFormContainer gap={12}>
                <EmailInput
                  ariaDescribedby="message-uat-forgot-password-email-input-field"
                  clearValue={() => setEmail('')}
                  disabled={isActionInProgress}
                  errorKey={errorKey}
                  isFocused
                  onChangeHandler={(e) => setEmail(e.target.value)}
                  value={email}
                />
                <TrackableButton
                  dataTestid="forgot-password-page-submit-button"
                  disabled={isActionInProgress || !isEmailValid(email)}
                  fullWidth
                  id="uat-forgot-password-button"
                  inProgress={isActionInProgress}
                  onClick={handleResetPassword}
                  title="Reset password button"
                  trackingId="reset_password_submit"
                  variant="primary"
                >
                  {t('forgot_password_page.button.reset_password')}
                </TrackableButton>
                {showMagicLink() && <MagicLink email={email} />}
              </ForgotPasswordPageFormContainer>
              <ForgotPasswordPageTermsContainer>
                <Text size="small" textAlign="center">
                  {t('forgot_password_page.terms_and_conditions.statement', {
                    privacyPolicyLink: PrivacyPolicyLink,
                    termsOfServiceLink,
                  })}
                </Text>
              </ForgotPasswordPageTermsContainer>
              <CaptchaPasswordReset
                setCaptchaToken={setCaptchaToken}
                shouldExecuteCaptcha={isButtonClicked}
              />
            </>
          )}
        </ForgotPasswordPageCard>
        {!isNativeMobile && <Footer />}
      </ForgotPasswordPageContainer>
    </>
  )
}
